export const PHONE_DISPLAY = "(707) 365-8992";
export const PHONE_LINK = "tel:+17073658992";
export const EMAIL = "philgrayd@yahoo.com";
export const EMAIL_LINK = `mailto:${EMAIL}`;
export const BUSINESS = "Phil Gray Landscaping";
export const LOCATION_LINES = ["Based in Dixon, California", "Serving Dixon, Davis, Vacaville, Winters & Woodland"];

export const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "";
// Inquiry form stays disabled until a Phil Gray recipient/backend is configured.
export const INQUIRY_ENDPOINT = process.env.REACT_APP_INQUIRY_ENDPOINT || "";

export const LOGO_BLACK = "/brand/phil-logo-black.webp";
export const LOGO_WHITE = "/brand/phil-logo-white.webp";
export const LOGO_LIGHT = "/brand/phil-logo-light.webp";
export const MARK_CIRCLE = "/brand/phil-mark-circle.png";

export const EASE = [0.22, 1, 0.36, 1];

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -12, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const openInquiry = (service = "") => {
  window.dispatchEvent(new CustomEvent("pgl:inquiry", { detail: { service } }));
};

export const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const TOP_STRIP = [
  "CUSTOM LANDSCAPE DESIGN",
  "PATIOS & WALKWAYS",
  "PLANTING & GARDENS",
  "IRRIGATION SOLUTIONS",
  "PERMACULTURE DESIGN",
];

export const CAPABILITY_LABELS = [
  "LANDSCAPE DESIGN",
  "PATIOS & WALKWAYS",
  "DROUGHT-TOLERANT PLANTING",
  "TREES & SHRUBS",
  "IRRIGATION SYSTEMS",
  "PERMACULTURE GARDENS",
  "DECKS & FENCES",
  "WATER FEATURES",
  "PERGOLAS & ARBORS",
];

export const INQUIRY_OPTIONS = [
  "Landscape design or full yard redesign",
  "Patio, walkway or hardscaping",
  "Planting or irrigation",
  "Permaculture garden",
  "Outdoor feature, deck or fence",
  "I'm still exploring my options",
];

const img = (name) => ({ src: `/images/${name}.webp`, srcSet: `/images/${name}-640.webp 640w, /images/${name}.webp 1400w` });

export const IMAGES = {
  brandTall: { ...img("brand-davis-garden"), alt: "Garden with large concrete pavers set in gravel, low planting beds and a white pergola" },
  brandSmall: { ...img("brand-stone-steps"), alt: "Stone steps and dry-stacked stone retaining walls beneath oak trees" },
  storyMain: { ...img("approach-backyard-pergola"), alt: "Backyard with wood pergolas over a patio beside a lawn and planting areas" },
  storySmall: { ...img("approach-stone-water-feature"), alt: "Stacked stone wall with a metal spillway water feature" },
};

export const SERVICES = [
  {
    num: "01",
    name: "LANDSCAPE DESIGN",
    title: "Give your yard a plan that brings it together.",
    copy: [
      "An awkward layout, an unused corner, or a front yard that needs a fresh start can be the reason to rethink your space. Begin with what you want to enjoy more often.",
      "Phil Gray Landscaping offers custom landscape design and installation, including drought-tolerant gardens and permaculture design. Explore planting and outdoor features as parts of one plan.",
      "Tell us what you want to keep, what you want to change, and how you would like to use the finished yard.",
    ],
    cta: "DISCUSS MY LANDSCAPE",
    inquiry: INQUIRY_OPTIONS[0],
    image: { ...img("service-front-yard"), alt: "Front yard with a curving concrete walkway, pink flowering beds and clipped shrubs" },
  },
  {
    num: "02",
    name: "PATIOS & HARDSCAPING",
    title: "Make room for the evenings you want outside.",
    copy: [
      "Picture a table with room around it, a path through the garden, or a place to settle in at the end of the day. Start with the moments you want your yard to make possible.",
      "Explore custom patios, walkways, retaining walls, decks, fences, pergolas, arbors, and water features with Phil Gray Landscaping.",
      "Share the area you want to improve and the look you have in mind. We can discuss the features that suit your project.",
    ],
    cta: "DISCUSS MY OUTDOOR SPACE",
    inquiry: INQUIRY_OPTIONS[1],
    image: { ...img("service-paver-patio"), alt: "Curved paver patio with low seating walls and boulders in a planted bed" },
  },
  {
    num: "03",
    name: "PLANTING & IRRIGATION",
    title: "Bring the garden into view.",
    copy: [
      "The planting beside your front walk. The view from the kitchen. The garden around your patio. Thoughtful plant choices can make the spaces you see every day more enjoyable.",
      "Our services include trees, shrubs, native and drought-tolerant planting, irrigation systems, and rainwater harvesting. Consider planting and watering together when planning your changes.",
      "Tell us what you like, what you find difficult to maintain, and any watering concerns you have noticed.",
    ],
    cta: "DISCUSS MY GARDEN",
    inquiry: INQUIRY_OPTIONS[2],
    image: { ...img("service-planting"), alt: "Planted slope with white flowering groundcover, ornamental grasses and shrubs" },
  },
];

export const FEATURES = [
  {
    title: "PATIOS & PATHS",
    tag: "OUTDOOR LIVING",
    copy: "Create a place to gather and connect the parts of your yard you use most.",
    inquiry: INQUIRY_OPTIONS[1],
    image: { ...img("feature-pergola-patio"), alt: "Wood pergola over a concrete patio with a table and chairs beside a lawn" },
  },
  {
    title: "DROUGHT-TOLERANT GARDENS",
    tag: "PLANTING",
    copy: "Explore planting with color, texture, and lower water demand as design priorities.",
    inquiry: INQUIRY_OPTIONS[2],
    image: { ...img("feature-planted-garden"), alt: "Mulched garden mound planted with ornamental grasses, shrubs and small trees" },
  },
  {
    title: "PERMACULTURE DESIGN",
    tag: "GARDEN DESIGN",
    copy: "Consider how soil, plants, and water can work together as your garden develops.",
    inquiry: INQUIRY_OPTIONS[3],
    image: { ...img("feature-circle-garden"), alt: "Backyard with a circular lawn ringed by flowering beds and a curved path" },
  },
];

export const BENEFITS = [
  { label: "A PLAN FOR YOUR PROPERTY", title: "Bring the separate pieces together.", copy: "Consider the planting, paths, seating areas, and watering as parts of the same outdoor space." },
  { label: "SPACE TO ENJOY", title: "Give everyday moments a place outside.", copy: "Make room for outdoor meals, quiet mornings, and time with the people you want to spend it with." },
  { label: "CARE YOU CAN PLAN FOR", title: "Make maintenance part of the conversation.", copy: "Explain which garden tasks you enjoy and which you want to reduce, so your choices reflect how you live." },
  { label: "THOUGHTFUL WATER USE", title: "Plan the planting and watering together.", copy: "Explore drought-tolerant plants and irrigation options suited to the garden you want to create." },
];

export const STEPS = [
  { num: "01", title: "SHOW US YOUR SPACE", text: "Tell us where your property is and which part of the yard you want to improve. Photos are helpful if you have them." },
  { num: "02", title: "SHARE WHAT MATTERS", text: "Describe how you want to use the space, what you want to keep, and what you would like to change." },
  { num: "03", title: "CHOOSE YOUR STARTING POINT", text: "Select the service closest to your project, whether that is a landscape redesign, hardscaping, planting, irrigation, or a permaculture garden." },
  { num: "04", title: "SEND YOUR PROJECT INQUIRY", text: "Include your questions, any budget considerations, and your preferred timing. Your inquiry begins the conversation about what comes next." },
];

export const FAQS = [
  { q: "What landscaping services do you offer?", a: ["Phil Gray Landscaping offers landscape design and installation, patios and walkways, retaining walls, planting, irrigation, permaculture design, and outdoor features such as decks, fences, pergolas, and water features."] },
  { q: "Can I improve one part of my yard?", a: ["Yes. You can inquire about a focused improvement or a larger redesign. Tell us which area you want to change and what you would like the project to achieve."] },
  { q: "Can you help me plan a patio or outdoor living space?", a: ["Yes. We offer patio and hardscape design and installation. Share how you want to use the space, the features you are considering, and any photographs that show the style you like."] },
  { q: "Do I need a finished design to contact you?", a: ["No. You can begin with ideas, photographs, or a description of what is not working in your current yard. Tell us what you want to change and where you would like help."] },
  { q: "Can I explore planting that needs less water?", a: ["Yes. Drought-tolerant planting and irrigation are among our services. Discuss the plant choices, establishment watering, and ongoing care for your particular space."] },
  { q: "What should I include in my inquiry?", a: ["Include your city, the area you want to improve, and the changes you have in mind. You can also mention your budget, preferred timing, and any features you want to keep.", "You do not need every detail decided before getting in touch."] },
  { q: "How much will my landscaping project cost?", a: ["Cost depends on the scope, site conditions, materials, and planting. Share your priorities and any budget you have in mind. Contact us to discuss assessment, design fees, and the next step for your project."] },
  { q: "Where do you work and how do I get started?", a: ["We serve Dixon, Davis, Vacaville, Winters, Woodland, and surrounding areas. Use the project inquiry form or call (707) 365-8992 to discuss your property and ideas."] },
];

export const FLOATING_NAV = [
  { id: "home", label: "HOME" },
  { id: "solutions", label: "SERVICES" },
  { id: "capabilities", label: "FEATURES" },
  { id: "faq", label: "FAQ" },
  { id: "contact", label: "CONTACT" },
];

export const FOOTER_NAV = [
  { id: "solutions", label: "Services" },
  { id: "capabilities", label: "Features" },
  { id: "approach", label: "Our Approach" },
  { id: "faq", label: "FAQ" },
  { id: "contact", label: "Contact" },
];

export const FOOTER_SERVICES = ["Landscape Design", "Patios & Hardscaping", "Planting & Irrigation", "Permaculture Gardens", "Outdoor Features"];

export const STARTERS = [
  "Explore landscape design",
  "Ask about patios or pathways",
  "I need help with planting or irrigation",
  "Start my project inquiry",
];
export const STARTER_OPEN_FORM = "Start my project inquiry";
